import type {
  AttachmentType,
  CertificateStatus,
  CertificateType,
  ProactiveAttachmentType,
  ProactiveProposalStatus,
  QuotationStatus,
  Role,
  VendorReviewAction,
  VendorReviewStatus,
  VendorAccountStatus,
  VendorStatus,
  VendorType,
} from './types';

export {
  VENDOR_DELIVERY_CYCLES,
  VENDOR_DELIVERY_SCOPES,
  VENDOR_FREIGHT_RULES,
  VENDOR_INVOICE_DELIVERY_METHODS,
  VENDOR_INVOICE_FORMATS,
  VENDOR_PAYMENT_METHODS,
  VENDOR_PAYMENT_TERMS,
  VENDOR_PRODUCT_CATEGORY_CODES,
  VENDOR_PRODUCT_CATEGORY_LABELS,
  VENDOR_RETURN_CONDITION_TYPES,
  VENDOR_RETURN_FREIGHT_RESPONSIBILITIES,
  VENDOR_RETURN_HANDLING_METHODS,
  VENDOR_WEEKDAYS,
} from './vendor-profile';

export const PRODUCT_CATEGORY_OPTIONS = [
  { code: 'A', name: '處方箋', sort_order: 1 },
  { code: 'B', name: 'OTC用藥', sort_order: 2 },
  { code: 'C', name: '甲類成藥（西藥）', sort_order: 3 },
  { code: 'D', name: '乙類成藥（西藥）', sort_order: 4 },
  { code: 'E', name: '甲類成藥（中藥）', sort_order: 5 },
  { code: 'F', name: '處方藥（中藥）', sort_order: 6 },
  { code: 'G', name: '乙類成藥（中藥）', sort_order: 7 },
  { code: 'H', name: '奶粉／液體營養配方', sort_order: 8 },
  { code: 'I', name: '尿布', sort_order: 9 },
  { code: 'J', name: '醫療器材', sort_order: 10 },
  { code: 'K', name: '美粧品（粧字碼）', sort_order: 11 },
  { code: 'L', name: '一般用品', sort_order: 12 },
  { code: 'M', name: '健康食品', sort_order: 13 },
  { code: 'N', name: '環境用藥', sort_order: 14 },
  { code: 'P', name: '惠市集', sort_order: 15 },
] as const;

export const PRODUCT_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  PRODUCT_CATEGORY_OPTIONS.map((item) => [item.code, item.name]),
);

export const VENDOR_APPLICATION_PAYMENT_TERM_LABELS: Record<string, string> = {
  PREPAID: '預付款',
  WEEKLY: '週結',
  CASH_ON_DELIVERY: '下貨收現',
  MONTHLY: '月結',
};

export const VENDOR_APPLICATION_PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: '現金',
  CHECK: '支票',
  BANK_TRANSFER: '匯款',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_DELIVERY_SCOPE_LABELS: Record<string, string> = {
  WAREHOUSE: '總倉',
  STORE: '門市',
  BOTH: '總倉與門市皆可',
};

export const VENDOR_APPLICATION_DELIVERY_CYCLE_LABELS: Record<string, string> = {
  DAILY: '每日',
  WEEKLY: '每週',
  FIXED_WEEKDAYS: '固定星期',
  BIWEEKLY: '隔週',
  MONTHLY: '每月',
  ORDER_NOTICE: '依訂單通知',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_WEEKDAY_LABELS: Record<string, string> = {
  MONDAY: '星期一',
  TUESDAY: '星期二',
  WEDNESDAY: '星期三',
  THURSDAY: '星期四',
  FRIDAY: '星期五',
  SATURDAY: '星期六',
  SUNDAY: '星期日',
};

export const VENDOR_APPLICATION_FREIGHT_RULE_LABELS: Record<string, string> = {
  FREE: '免運',
  THRESHOLD: '達指定金額免運',
  FIXED: '固定運費',
  ACTUAL: '依實際物流費',
  VENDOR_PAYS: '廠商負擔',
  COMPANY_PAYS: '我司負擔',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_RETURN_HANDLING_LABELS: Record<string, string> = {
  RETURN: '退貨',
  EXCHANGE: '換貨',
  RETURN_OR_EXCHANGE: '退貨或換貨皆可',
  NONE: '不接受退換貨',
};

export const VENDOR_APPLICATION_RETURN_CONDITION_LABELS: Record<string, string> = {
  NEAR_EXPIRY: '即期',
  DEFECT: '瑕疵',
  INTOLERANCE: '吃不合',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_RETURN_FREIGHT_LABELS: Record<string, string> = {
  VENDOR: '廠商負擔',
  COMPANY: '我司負擔',
  BY_REASON: '依退換貨原因判定',
  AGREEMENT: '雙方協議',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_INVOICE_FORMAT_LABELS: Record<string, string> = {
  ELECTRONIC: '電子發票',
  PAPER_2: '紙本二聯式',
  PAPER_3: '紙本三聯式',
  E_CALCULATOR: '電子計算機統一發票',
  CASH_REGISTER: '收銀機統一發票',
  EXEMPT: '免用統一發票',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_INVOICE_DELIVERY_LABELS: Record<string, string> = {
  WITH_GOODS: '隨貨附發票',
  MAIL: '郵寄發票',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_RECONCILIATION_NOTICE =
  '我司對帳作業原則：每月第五個工作天寄送對帳單及出貨相關資料，亦可由對帳人員親送至總倉辦理；若超過當月對帳期限，則併入次月帳務處理。';

export const VENDOR_APPLICATION_CHECK_NOTICE = '我司支票兌現日為每月30日。';

export const VENDOR_APPLICATION_BANK_TRANSFER_NOTICE =
  '我司匯款日為每月月底；如遇假日，順延至第一個工作天辦理。';

export function formatProductCategory(
  code?: string | null,
  name?: string | null,
  legacyName = '',
): string {
  const normalizedCode = code?.trim() ?? '';
  const resolvedName = name?.trim() || PRODUCT_CATEGORY_LABELS[normalizedCode] || legacyName.trim();
  return normalizedCode && resolvedName
    ? `${normalizedCode}｜${resolvedName}`
    : resolvedName || normalizedCode || legacyName || '—';
}

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: '系統管理員',
  BUYER: '採購人員',
  STORE: '門市人員',
  VENDOR: '廠商填報者',
  FINANCE: '財務人員',
};

export const VENDOR_STATUS_LABELS: Record<VendorStatus, string> = {
  DRAFT: '草稿',
  ACTIVE: '啟用',
  SUSPENDED: '暫停交易',
  INACTIVE: '停用',
};

export const VENDOR_STATUS_TONE: Record<VendorStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  SUSPENDED: 'bg-orange-100 text-orange-900',
  INACTIVE: 'bg-rose-100 text-rose-800',
};

export const VENDOR_ACCOUNT_STATUS_LABELS: Record<VendorAccountStatus, string> = {
  NOT_INITIALIZED: '尚未設定密碼',
  ACTIVE: '啟用',
  PASSWORD_RESET_REQUIRED: '密碼需重設',
  LOCKED: '已鎖定',
  DISABLED: '已停用',
};

export const VENDOR_ACCOUNT_STATUS_TONE: Record<VendorAccountStatus, string> = {
  NOT_INITIALIZED: 'bg-amber-100 text-amber-900',
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  PASSWORD_RESET_REQUIRED: 'bg-orange-100 text-orange-900',
  LOCKED: 'bg-orange-100 text-orange-900',
  DISABLED: 'bg-rose-100 text-rose-800',
};

export const VENDOR_REVIEW_STATUS_LABELS: Record<VendorReviewStatus, string> = {
  DRAFT: '草稿',
  PENDING_REVIEW: '待採購審核',
  IN_REVIEW: '採購審核中',
  DOCUMENTS_REQUIRED: '要求補件',
  PENDING_RE_REVIEW: '待重新審核',
  APPROVED: '審核通過',
  REJECTED: '審核不通過',
};

export const VENDOR_REVIEW_STATUS_TONE: Record<VendorReviewStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING_REVIEW: 'bg-sky-100 text-sky-800',
  IN_REVIEW: 'bg-amber-100 text-amber-900',
  DOCUMENTS_REQUIRED: 'bg-orange-100 text-orange-900',
  PENDING_RE_REVIEW: 'bg-cyan-100 text-cyan-900',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-rose-100 text-rose-800',
};

export const VENDOR_REVIEW_ACTION_LABELS: Record<VendorReviewAction, string> = {
  APPROVE: '審核通過',
  REQUEST_DOCUMENTS: '要求補件',
  RETURN_FOR_EDIT: '退回修改',
  REJECT: '審核不通過',
};

export const VENDOR_TYPE_LABELS: Record<VendorType, string> = {
  MANUFACTURER: '製造商',
  DISTRIBUTOR: '經銷商',
  AGENT: '代理商',
  IMPORTER: '進口商',
  WHOLESALER: '批發商',
  OTHER: '其他',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: '銀行匯款',
  CHECK: '支票',
  CASH: '現金',
  ACH_TRANSFER: 'ACH／轉帳',
  OTHER: '其他',
};

export const WAREHOUSE_DELIVERY_METHOD_LABELS: Record<string, string> = {
  SUPPLIER_DIRECT_TO_WAREHOUSE: '廠商直送總倉',
  LOGISTICS_TO_WAREHOUSE: '物流配送總倉',
  COURIER: '宅配',
  FREIGHT: '貨運',
  REFRIGERATED: '冷藏配送',
  FROZEN: '冷凍配送',
  SELF_PICKUP: '自取',
  OTHER: '其他',
};

export const STORE_DELIVERY_METHOD_LABELS: Record<string, string> = {
  SUPPLIER_DIRECT_TO_STORE: '廠商直送門市',
  SUPPLIER_LOGISTICS: '廠商物流',
  COURIER: '宅配',
  FREIGHT: '貨運',
  REFRIGERATED: '冷藏配送',
  FROZEN: '冷凍配送',
  OTHER: '其他',
};

export const WAREHOUSE_RETURN_METHOD_LABELS: Record<string, string> = {
  SUPPLIER_PICKUP: '廠商到總倉取回',
  RETURN_WITH_NEXT_DELIVERY: '隨下一次送貨退回',
  LOGISTICS_RETURN: '物流寄回',
  COURIER_RETURN: '宅配退回',
  FREIGHT_RETURN: '貨運退回',
  COMPANY_DELIVERY: '公司自行送回',
  SCRAP_CREDIT: '報廢後折讓',
  OTHER: '其他',
};

export const STORE_RETURN_METHOD_LABELS: Record<string, string> = {
  SUPPLIER_PICKUP_AT_STORE: '廠商至門市取回',
  PICKUP_WITH_DELIVERY: '配送時順便取回',
  COURIER_FROM_STORE: '門市宅配寄回',
  FREIGHT_FROM_STORE: '門市貨運寄回',
  OTHER: '其他',
};

export const INVOICE_FORMAT_LABELS: Record<string, string> = {
  ELECTRONIC: '電子發票',
  PAPER_DUPLICATE: '紙本二聯式發票',
  PAPER_TRIPLICATE: '紙本三聯式發票',
  ELECTRONIC_CALCULATOR: '電子計算機統一發票',
  CASH_REGISTER: '收銀機統一發票',
  EXEMPT: '免用統一發票',
  OTHER: '其他',
};

export const INVOICE_ISSUE_METHOD_LABELS: Record<string, string> = {
  PER_SHIPMENT: '每張出貨單開立',
  PER_PURCHASE_ORDER: '每張採購單開立',
  PER_DELIVERY: '每次配送開立',
  MONTHLY_SUMMARY: '月結彙總開立',
  AFTER_RECONCILIATION: '對帳後開立',
  OTHER: '其他',
};

export const INVOICE_DELIVERY_METHOD_LABELS: Record<string, string> = {
  WITH_GOODS: '隨貨附發票',
  POST: '郵寄',
  EMAIL: 'Email',
  E_INVOICE_PLATFORM: '電子發票平台',
  MONTHLY_MAIL: '月結統一寄送',
  OTHER: '其他',
};

export const VENDOR_ATTACHMENT_LABELS: Record<string, string> = {
  BUSINESS_LICENSE: '公司／營業登記證明',
  BUSINESS_REGISTRATION: '公司／商業登記資料',
  TAX_ID_CERTIFICATE: '統一編號相關證明',
  BANK_CERTIFICATE: '銀行帳戶證明',
  COMPANY_PROFILE: '公司基本資料文件',
  COMPANY_CATALOG: '公司介紹／型錄',
  CONTRACT: '合約或交易條件文件',
  PRODUCT_LIABILITY_INSURANCE: '產品責任保險資料',
  PRODUCT_CERTIFICATE: '商品或法規證明',
  OTHER_DOCUMENT: '其他證明文件',
  OTHER_REQUIRED: '其他必要附件',
  OTHER: '其他附件',
};

export const VENDOR_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  ...VENDOR_ATTACHMENT_LABELS,
};

export const STATUS_LABELS: Record<QuotationStatus, string> = {
  DRAFT: '草稿',
  WAITING_VENDOR: '等待廠商填寫',
  VENDOR_FILLING: '廠商填寫中',
  VENDOR_SUBMITTED: '廠商已提交',
  BUYER_REVIEW: '採購初審',
  REVISION_REQUIRED: '要求補件',
  MEETING_REVIEW: '會議審議',
  APPROVED: '核准導入',
  PAUSED: '暫緩',
  NOT_ADOPTED: '不採用',
  MASTER_CREATED: '已建立商品主檔',
};

export const STATUS_TONE: Record<QuotationStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  WAITING_VENDOR: 'bg-sky-100 text-sky-800',
  VENDOR_FILLING: 'bg-indigo-100 text-indigo-800',
  VENDOR_SUBMITTED: 'bg-violet-100 text-violet-800',
  BUYER_REVIEW: 'bg-amber-100 text-amber-900',
  REVISION_REQUIRED: 'bg-orange-100 text-orange-900',
  MEETING_REVIEW: 'bg-purple-100 text-purple-900',
  APPROVED: 'bg-emerald-100 text-emerald-900',
  PAUSED: 'bg-yellow-100 text-yellow-900',
  NOT_ADOPTED: 'bg-rose-100 text-rose-900',
  MASTER_CREATED: 'bg-teal-100 text-teal-900',
};

export const PROACTIVE_STATUS_LABELS: Record<ProactiveProposalStatus, string> = {
  DRAFT: '草稿',
  WAITING_VENDOR_APPROVAL: '等待廠商資格核准',
  SUBMITTED: '待採購審核',
  WAITING_INITIAL_SCREEN: '待採購審核',
  BUYER_REVIEW: '待採購審核',
  REVISION_REQUIRED: '要求補件',
  VENDOR_REVISING: '廠商補件中',
  WAITING_RE_REVIEW: '待採購審核',
  PASSED_INITIAL_SCREEN: '等待會議審議',
  WAITING_MEETING: '等待會議審議',
  APPROVED: '決議上市',
  PAUSED: '暫緩',
  NOT_ADOPTED: '不採用',
  CONVERTED_TO_FORMAL_CASE: '決議上市',
  MASTER_CREATED: '已建立商品主檔',
};

export const PROACTIVE_STATUS_TONE: Record<ProactiveProposalStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  WAITING_VENDOR_APPROVAL: 'bg-amber-100 text-amber-900',
  SUBMITTED: 'bg-violet-100 text-violet-800',
  WAITING_INITIAL_SCREEN: 'bg-sky-100 text-sky-800',
  BUYER_REVIEW: 'bg-amber-100 text-amber-900',
  REVISION_REQUIRED: 'bg-orange-100 text-orange-900',
  VENDOR_REVISING: 'bg-indigo-100 text-indigo-800',
  WAITING_RE_REVIEW: 'bg-cyan-100 text-cyan-900',
  PASSED_INITIAL_SCREEN: 'bg-emerald-100 text-emerald-900',
  WAITING_MEETING: 'bg-purple-100 text-purple-900',
  APPROVED: 'bg-emerald-100 text-emerald-900',
  PAUSED: 'bg-yellow-100 text-yellow-900',
  NOT_ADOPTED: 'bg-rose-100 text-rose-900',
  CONVERTED_TO_FORMAL_CASE: 'bg-teal-100 text-teal-900',
  MASTER_CREATED: 'bg-teal-100 text-teal-900',
};

export const LAUNCH_ANNOUNCEMENT_STATUS_LABELS = {
  DRAFT: '草稿',
  PENDING_PUBLICATION: '待發布',
  PUBLISHED: '已發布',
  WITHDRAWN: '已撤回',
  ARCHIVED: '已封存',
} as const;

export const LAUNCH_ANNOUNCEMENT_STATUS_TONE = {
  DRAFT: 'bg-slate-100 text-slate-700',
  PENDING_PUBLICATION: 'bg-amber-100 text-amber-900',
  PUBLISHED: 'bg-emerald-100 text-emerald-900',
  WITHDRAWN: 'bg-rose-100 text-rose-900',
  ARCHIVED: 'bg-slate-200 text-slate-700',
} as const;

export const PRODUCT_MASTER_WORK_STATUS_LABELS = {
  PENDING: '待建立商品主檔',
  IN_PROGRESS: '建檔中',
  DATA_INCOMPLETE: '資料待補',
  PRODUCT_MASTER_CREATED: '已建立商品主檔',
  ANNOUNCEMENT_LINKED: '已加入上市公告',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
} as const;

export const PRODUCT_MASTER_WORK_STATUS_TONE = {
  PENDING: 'bg-amber-100 text-amber-900',
  IN_PROGRESS: 'bg-sky-100 text-sky-900',
  DATA_INCOMPLETE: 'bg-rose-100 text-rose-900',
  PRODUCT_MASTER_CREATED: 'bg-teal-100 text-teal-900',
  ANNOUNCEMENT_LINKED: 'bg-violet-100 text-violet-900',
  COMPLETED: 'bg-emerald-100 text-emerald-900',
  CANCELLED: 'bg-slate-200 text-slate-700',
} as const;

export const PRODUCT_MASTER_ANNOUNCEMENT_PROGRESS_LABELS = {
  NOT_CREATED: '尚未建立公告',
  DRAFT: '已加入公告草稿',
  PENDING_PUBLICATION: '公告待發布',
  PUBLISHED: '公告已發布',
  WITHDRAWN: '公告已撤回',
  ARCHIVED: '公告已封存',
} as const;

export const LAUNCH_ANNOUNCEMENT_AUDIENCE_LABELS = {
  ALL: '全門市',
  DESIGNATED: '指定門市',
} as const;

export const ATTACHMENT_LABELS: Record<AttachmentType, string> = {
  PRODUCT_FRONT: '商品正面照',
  PRODUCT_BACK: '商品背面照',
  BARCODE_PHOTO: '條碼照片',
  QUOTATION: '報價單',
  INSPECTION_REPORT: '檢驗報告',
  LICENSE: '許可證明',
  PRESENTATION: '商品簡報',
  OTHER: '其他附件',
};

export const PROACTIVE_ATTACHMENT_LABELS: Record<ProactiveAttachmentType, string> = {
  PRODUCT_FRONT: '商品正面圖片',
  PRODUCT_BACK: '商品背面圖片',
  PRODUCT_SIDE: '商品側面照',
  BARCODE_IMAGE: '條碼圖片',
  BARCODE_PHOTO: '條碼圖片',
  PRODUCT_CATALOG: '商品型錄',
  QUOTATION: '報價單',
  PRESENTATION: '商品簡報',
  INSPECTION_REPORT: '檢驗報告',
  LICENSE: '許可證／登錄資料',
  CERTIFICATION: '認證資料',
  INGREDIENTS: '成分資料',
  PRODUCT_LIABILITY_INSURANCE: '產品責任險資料',
  ADVERTISING: '廣告素材',
  OTHER: '其他附件',
};

const PROACTIVE_ATTACHMENT_CODE_LABELS: Record<string, string> = {
  ...PROACTIVE_ATTACHMENT_LABELS,
  PRODUCT_FRONT_IMAGE: '商品正面圖片',
  PRODUCT_BACK_IMAGE: '商品背面圖片',
  BARCODE_PHOTO: '條碼圖片',
  PRODUCT_PRESENTATION: '商品簡報',
  INGREDIENT_DOCUMENT: '成分資料',
  LICENSE_DOCUMENT: '許可證／登錄資料',
  PRODUCT_LIABILITY_INSURANCE: '產品責任險資料',
  LIABILITY_INSURANCE: '產品責任險資料',
};

export function labelProactiveAttachmentType(type: unknown, configuredLabel?: unknown): string {
  const code = String(type ?? '').trim();
  const configured = String(configuredLabel ?? '').trim();
  const mapped = PROACTIVE_ATTACHMENT_CODE_LABELS[code];
  if (configured && !/^[A-Z][A-Z0-9_]*$/.test(configured)) return configured;
  return mapped ?? '其他附件';
}

export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  HAS_CERTIFICATE: '有認證／許可證',
  NOT_REQUIRED: '依法無需認證／許可證',
};

export const CERTIFICATE_TYPE_LABELS: Record<CertificateType, string> = {
  DRUG_LICENSE: '藥品許可證',
  TCM_LICENSE: '中藥許可證',
  MEDICAL_DEVICE: '醫療器材許可證／登錄',
  HEALTH_FOOD: '健康食品許可證',
  FOOD_REGISTRATION: '食品相關認證／登錄',
  COSMETIC: '化粧品相關登錄',
  ENVIRONMENTAL_AGENT: '環境用藥許可證',
  OTHER: '其他認證／許可證',
};

export function labelCertificateStatus(status?: string | null): string {
  if (!status) return '—';
  return CERTIFICATE_STATUS_LABELS[status as CertificateStatus] ?? status;
}

export function labelCertificateType(type?: string | null): string {
  if (!type) return '—';
  return CERTIFICATE_TYPE_LABELS[type as CertificateType] ?? type;
}

export const PROACTIVE_CHANNEL_LABELS: Record<string, string> = {
  COMPANY_ALL_STORES: '本公司全門市',
  COMPANY_DESIGNATED_STORES: '本公司指定門市',
  PHARMACY: '藥局',
  CLINIC: '診所',
  HOSPITAL: '醫院',
  ECOMMERCE: '電商',
  DEPARTMENT_STORE: '百貨',
  SUPERMARKET: '超市',
  HYPERMARKET: '量販店',
  CONVENIENCE: '便利商店',
  BEAUTY: '美妝通路',
  OFFICIAL_WEBSITE: '官方網站',
  OTHER: '其他',
};

export const PROACTIVE_LIFECYCLE_LABELS: Record<string, string> = {
  NEW: '全新上市',
  GROWTH: '成長期商品',
  MATURE: '成熟期商品',
  EXISTING: '既有常態商品',
  ALREADY_LISTED: '已上市',
  REVISED: '改版',
  NEW_SPEC: '新規格',
  NEW_PACKAGE: '新包裝',
  LIMITED: '限定商品',
  SEASONAL: '季節商品',
  DISCONTINUING: '即將停產商品',
  RESUBMITTED: '重新提案',
  OTHER: '其他',
};

export const VENDOR_APPLICATION_ATTACHMENT_LABELS: Record<string, string> = {
  BUSINESS_REGISTRATION: '公司／商業登記資料',
  TAX_ID_CERTIFICATE: '統一編號相關證明',
  BANK_CERTIFICATE: '銀行帳戶證明',
  COMPANY_PROFILE: '公司基本資料文件',
  COMPANY_CATALOG: '公司介紹／型錄',
  CONTRACT: '合約文件',
  PRODUCT_LIABILITY_INSURANCE: '產品責任保險資料',
  OTHER_DOCUMENT: '其他證明文件',
  OTHER_REQUIRED: '其他公司規定之必要文件',
  OTHER: '其他附件',
};

export const PROACTIVE_REVISION_ITEM_LABELS: Record<string, string> = {
  PRODUCT_BASIC: '商品基本資料',
  PRODUCT_CATEGORY: '商品分類',
  PRODUCT_SUBCATEGORY: '商品子分類',
  BARCODES: '條碼資料',
  PRICES: '價格資料',
  TRADE_TERMS: '交易條件',
  INITIAL_OFFER: '首批優惠',
  GIFTS: '贈品及搭贈',
  PROMOTION: '促銷支援',
  TRIAL_SUPPORT: '試用／體驗支援',
  SALES_PLAN: '銷售與上市提案',
  PRODUCT_FRONT_IMAGE: '商品正面圖片',
  PRODUCT_BACK_IMAGE: '商品背面圖片',
  BARCODE_IMAGE: '條碼圖片',
  QUOTATION: '報價單',
  INSPECTION_REPORT: '檢驗報告',
  LICENSE: '許可證／登錄資料',
  OTHER_ATTACHMENT: '其他附件',
  OTHER: '其他',
};

export const PROACTIVE_REJECTION_REASONS: Record<string, string> = {
  DUPLICATE_PRODUCT: '商品重複或已有同類商品',
  MARGIN_TOO_LOW: '毛利不符合採購條件',
  MARKET_FIT_LOW: '市場定位或客群不符',
  DOCUMENT_INCOMPLETE: '文件或法規資料不足',
  MOQ_UNSUITABLE: '最低訂購量或交易條件不合適',
  DELIVERY_UNSUITABLE: '交貨或配送條件不合適',
  OTHER: '其他原因',
};

export const REQUIRED_PRODUCT_FIELDS: Array<keyof import('./types').SubmissionProduct> = [
  'name_zh',
  'brand',
  'product_category_code',
  'specification',
  'unit',
  'origin',
  'case_pack',
  'shelf_life',
  'ex_tax_price',
  'tax_included_price',
  'tax_rate',
  'suggested_retail_price',
  'minimum_order_quantity',
  'minimum_delivery_unit',
  'minimum_order_amount',
];

export const STATUS_TRANSITIONS: Record<QuotationStatus, QuotationStatus[]> = {
  DRAFT: ['WAITING_VENDOR'],
  WAITING_VENDOR: ['VENDOR_FILLING', 'DRAFT'],
  VENDOR_FILLING: ['VENDOR_SUBMITTED', 'REVISION_REQUIRED'],
  VENDOR_SUBMITTED: ['BUYER_REVIEW'],
  BUYER_REVIEW: ['REVISION_REQUIRED', 'MEETING_REVIEW', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  REVISION_REQUIRED: ['VENDOR_FILLING'],
  MEETING_REVIEW: ['APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  APPROVED: ['MASTER_CREATED'],
  PAUSED: ['MEETING_REVIEW', 'APPROVED', 'NOT_ADOPTED'],
  NOT_ADOPTED: ['MEETING_REVIEW'],
  MASTER_CREATED: [],
};

export const PROACTIVE_STATUS_TRANSITIONS: Record<
  ProactiveProposalStatus,
  ProactiveProposalStatus[]
> = {
  DRAFT: ['SUBMITTED', 'WAITING_INITIAL_SCREEN'],
  WAITING_VENDOR_APPROVAL: ['WAITING_INITIAL_SCREEN', 'REVISION_REQUIRED'],
  SUBMITTED: ['REVISION_REQUIRED', 'WAITING_MEETING', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  WAITING_INITIAL_SCREEN: [
    'REVISION_REQUIRED',
    'WAITING_MEETING',
    'APPROVED',
    'PAUSED',
    'NOT_ADOPTED',
  ],
  BUYER_REVIEW: ['REVISION_REQUIRED', 'WAITING_MEETING', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  REVISION_REQUIRED: ['VENDOR_REVISING'],
  VENDOR_REVISING: ['WAITING_RE_REVIEW', 'WAITING_INITIAL_SCREEN'],
  WAITING_RE_REVIEW: ['REVISION_REQUIRED', 'WAITING_MEETING', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  PASSED_INITIAL_SCREEN: ['WAITING_MEETING', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  WAITING_MEETING: ['REVISION_REQUIRED', 'APPROVED', 'PAUSED', 'NOT_ADOPTED'],
  APPROVED: ['MASTER_CREATED'],
  PAUSED: ['REVISION_REQUIRED', 'WAITING_MEETING', 'APPROVED', 'NOT_ADOPTED'],
  NOT_ADOPTED: [],
  CONVERTED_TO_FORMAL_CASE: ['MASTER_CREATED'],
  MASTER_CREATED: [],
};

export const ROLE_PERMISSIONS: Record<Role, string[]> = {
  ADMIN: ['*'],
  BUYER: [
    'round:read',
    'round:write',
    'vendor:read',
    'vendor:write',
    'invite:write',
    'submission:read',
    'submission:review',
    'submission:status',
    'meeting:write',
    'report:read',
    'proactive:read',
    'proactive:write',
  ],
  STORE: ['round:read', 'submission:read', 'comment:write', 'report:read'],
  VENDOR: ['vendor-submission:read', 'vendor-submission:write'],
  FINANCE: ['vendor:read', 'vendor:financial-write', 'report:read'],
};

export const ALLOWED_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
] as const;

export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_COUNT = 20;

export const DEFAULT_SETTINGS = {
  currency: 'TWD',
  default_tax_rate: 5,
  margin_calculation: 'RETAIL_MINUS_COST' as const,
  max_attachment_size_mb: 10,
  max_attachment_count: 20,
  proactive_enabled: true,
  proactive_daily_max: 100,
  proactive_vendor_daily_max: 10,
  proactive_max_file_size_mb: 10,
  proactive_max_file_count: 20,
  proactive_allowed_file_types: [...ALLOWED_ATTACHMENT_TYPES],
  proactive_completeness_threshold: 70,
  proactive_numbering_rule: 'NP-{YYYY}-{SEQ6}',
  proactive_allow_new_vendors: true,
  proactive_require_email_verification: true,
};

export const PRODUCT_STRATEGY_LABELS: Record<string, string> = {
  REPLACE: '取代既有商品',
  COEXIST: '與既有商品並行',
  TRIAL: '試賣',
  LIMITED_STORE: '限定門市',
  NEW_CATEGORY: '新增品項，不取代既有商品',
  REJECT: '不導入',
};

export const REPLACED_PRODUCT_HANDLING_LABELS: Record<string, string> = {
  SELL_OUT: '售完不補',
  RETURN_TO_SUPPLIER: '退回供應商',
  STORE_TRANSFER: '跨店調撥後去化',
  PROMO_CLEARANCE: '促銷去化',
  OTHER: '其他',
};

export const COMPARISON_REPLACEMENT_LABELS: Record<string, string> = {
  REPLACE_PRIMARY: '建議取代主要競品',
  COEXIST: '建議與既有商品並行販售',
  TRIAL: '建議限定期間試賣',
  LIMITED_STORE: '建議限定門市試賣',
  REJECT: '不建議導入',
  PENDING_MEETING: '待會議決議',
};

export function labelProductStrategy(strategy?: string | null): string {
  if (!strategy) return '未設定';
  return PRODUCT_STRATEGY_LABELS[strategy] || strategy;
}

export function labelReplacedProductHandling(handling?: string | null): string {
  if (!handling) return '未設定';
  return REPLACED_PRODUCT_HANDLING_LABELS[handling] || handling;
}

export function labelComparisonReplacement(assessment?: string | null): string {
  if (!assessment) return '待會議決議';
  return COMPARISON_REPLACEMENT_LABELS[assessment] || assessment;
}
